import { t } from "./i18n";
// Preparación de fotos en el navegador, antes de subirlas.
//
// Por qué se achica acá y no en el servidor:
//
//   1. COSTO. El precio de la API escala con el tamaño de la imagen.
//      Una foto de celular de 4000px son ~21.000 tokens; a 1024px son
//      ~1.400. Quince veces más barato, y clasificar "jean azul, denim"
//      no necesita resolución de catálogo.
//
//   2. TIEMPO. Subir 20 fotos de 4 MB por datos móviles es inviable.
//      Achicadas pesan ~124 KB cada una.
//
//      ⚠️  ACÁ DECÍA "~200 KB", Y ERA UNA ESTIMACIÓN VESTIDA DE DATO.
//
//      Medido el 2026-08-25 sobre fotos reales de prendas, leyendo
//      `storage.objects.metadata->>'size'`: 126.474 y 122.418 bytes.
//      124 KB, no 200. El número viejo estaba 38% arriba.
//
//      No fue inocuo: toda la cuenta de egreso de Supabase se armó
//      sobre él y dio US$492/mes cuando el real era US$313. Un
//      comentario se lee con MÁS confianza que un cálculo, porque
//      parece que alguien ya lo verificó. Si un número de un comentario
//      no dice cómo se midió, no está medido.
//
//   3. Sin dependencias nativas. Hacerlo en el servidor pedía `sharp`,
//      que compila nativo y en Windows es una fuente conocida de dolor.
//
// El servidor igual valida tamaño y tipo: esto es una optimización, no
// un control de seguridad. Nada de lo que pase acá se da por bueno allá.

// 1024px de lado mayor. Suficiente para ver la prenda y para que la IA
// la clasifique; más es pagar tokens por píxeles que nadie mira.
const LADO_MAXIMO = 1024;

// 0.85 es el punto donde el JPEG deja de mejorar a la vista pero sigue
// engordando el archivo.
const CALIDAD = 0.85;

/* ══════════════════════════════════════════════════════════════════
   LA MINIATURA — el 88% del egreso de Supabase

   La grilla del armario muestra la foto de 1024px dentro de un
   casillero de ~200px. Se bajan 124 KB para pintar 200 píxeles de
   lado. Con 30.000 usuarios abriendo el armario 10 veces al mes son
   30M descargas: US$313/mes de egreso. Con miniaturas de 512px,
   US$37. La cuenta entera está en supabase/foto_mini.sql, junto con
   por qué NO se usa la transformación de imágenes de Supabase
   (facturaría US$15.000/mes: 30 veces el problema que arregla).

   ── POR QUÉ 512 ────────────────────────────────────────────────────

   Medido sobre el CSS, no estimado. `.grilla-prendas` es
   `minmax(200px, 1fr)` con `auto-fill` dentro de un contenedor de
   1180px: entran 5 columnas y cada celda queda en ~213px CSS. En
   celular son 2 columnas de ~193px. Una pantalla retina necesita el
   doble de píxeles reales que CSS: 426. Un celular DPR3, ~580.

   512 cubre entero el caso de PC-retina y el 88% del celular más
   exigente. Los 384 que había antes quedaban cortos en los dos.

   ⚠️  EL CASILLERO DE 320px NO ENTRA EN ESTA CUENTA. Es
       `.grilla-outfits` (sistema.css), y adentro van TRES prendas en
       fila: ~107px cada una. La primera versión de este comentario lo
       usó como el caso exigente y por eso eligió 384. Estaba mirando
       el número más grande del CSS sin preguntarse qué había adentro.

   ── EL PRECIO DE CADA TAMAÑO, CON EL PESO YA MEDIDO ────────────────

       384px   ~12 KB   US$11/mes
       512px   ~22 KB   US$37/mes   ← elegido
       576px   ~28 KB   US$53/mes
       hoy    ~124 KB   US$313/mes

   La diferencia entre el más barato y el más caro son US$42/mes, al
   lado de US$276 de ahorro. No valía la pena optimizar ahí, y sí
   valía la pena que ninguna foto se viera blanda.

   ⚠️  12 KB ES MEDIDO; 22 y 28 SON EXTRAPOLADOS con bytes ∝ píxeles.
       La única medición real —384px pesó 9,95% de la grande, cuando
       por píxeles daría 14%— dice que el modelo SOBREestima. El error
       va para el lado seguro: el costo real va a ser menor que el de
       la tabla, no mayor. El log `🖼️` de lib/rutas_prendas.js lo
       confirma con la primera tanda que se suba con 512.
   ══════════════════════════════════════════════════════════════════ */
const LADO_MINIATURA = 512;
const CALIDAD_MINI = 0.72;

const TIPOS_ACEPTADOS = ["image/jpeg", "image/png", "image/webp"];

// El iPhone saca HEIC por defecto y el navegador no lo sabe decodificar.
// Vale detectarlo para dar un mensaje entendible en vez de "imagen
// corrupta".
const TIPOS_CONOCIDOS_NO_SOPORTADOS = {
  "image/heic": "HEIC (formato de iPhone)",
  "image/heif": "HEIF (formato de iPhone)",
};

export class ErrorDeImagen extends Error {}

/**
 * Dibuja `origen` achicado a `lado` y lo devuelve como JPEG.
 *
 * Existe como función aparte para que la foto grande y la miniatura
 * pasen por EL MISMO camino: mismo fondo blanco, mismo suavizado, misma
 * conversión. Cuando eran dos bloques copiados, cualquier arreglo en uno
 * dejaba al otro atrás sin que nada lo dijera.
 *
 * @param {ImageBitmap|HTMLCanvasElement} origen
 * @param {number} lado    lado mayor del resultado
 * @param {number} calidad 0–1 para el JPEG
 */
function dibujar(origen, lado, calidad) {
  const anchoOrigen = origen.width;
  const altoOrigen = origen.height;

  const escala = Math.min(1, lado / Math.max(anchoOrigen, altoOrigen));
  const ancho = Math.max(1, Math.round(anchoOrigen * escala));
  const alto = Math.max(1, Math.round(altoOrigen * escala));

  const lienzo = document.createElement("canvas");
  lienzo.width = ancho;
  lienzo.height = alto;

  const ctx = lienzo.getContext("2d");

  // Fondo blanco: un PNG con transparencia, pasado a JPEG, queda con el
  // fondo negro. Una remera blanca sobre negro confunde a cualquiera.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, ancho, alto);

  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(origen, 0, 0, ancho, alto);

  const dataUrl = lienzo.toDataURL("image/jpeg", calidad);
  const base64 = dataUrl.split(",")[1] || "";

  return {
    lienzo,
    dataUrl,
    base64,
    ancho,
    alto,
    // Longitud aproximada en bytes del base64 decodificado
    bytes: Math.round((base64.length * 3) / 4),
  };
}

/**
 * Achica una foto y la devuelve lista para mandar al backend.
 *
 * @param {File} archivo
 * @returns {{ base64, contentType, ancho, alto, bytes, vistaPrevia, mini }}
 */
export async function prepararFoto(archivo) {
  const noSoportado = TIPOS_CONOCIDOS_NO_SOPORTADOS[archivo.type];
  if (noSoportado) {
    throw new ErrorDeImagen(
      t("comun.formatoNoSoportado", "{archivo} está en {formato}. Conviértela a JPG antes de subirla.", {
        archivo: archivo.name,
        formato: noSoportado,
      })
    );
  }

  if (!TIPOS_ACEPTADOS.includes(archivo.type)) {
    throw new ErrorDeImagen(
      t("comun.tipoNoAceptado", "{archivo}: solo se aceptan JPG, PNG y WEBP.", {
        archivo: archivo.name,
      })
    );
  }

  let bitmap;
  try {
    // imageOrientation "from-image" respeta el EXIF. Sin esto, las fotos
    // sacadas de costado con el celular llegan rotadas y la IA clasifica
    // una prenda acostada.
    bitmap = await createImageBitmap(archivo, { imageOrientation: "from-image" });
  } catch {
    throw new ErrorDeImagen(
      t("comun.noEsImagen", "{archivo} no se pudo leer como imagen.", {
        archivo: archivo.name,
      })
    );
  }

  const grande = dibujar(bitmap, LADO_MAXIMO, CALIDAD);
  bitmap.close?.();

  if (!grande.base64) {
    throw new ErrorDeImagen(
      t("comun.noSeConvirtio", "{archivo} no se pudo convertir.", {
        archivo: archivo.name,
      })
    );
  }

  /* La miniatura sale del lienzo GRANDE, no del bitmap original.
   *
   * Achicar 4000px → 512px de un saque deja escalones: el navegador
   * muestrea el original y se saltea detalle. Bajar en dos pasos
   * (4000 → 1024 → 512) promedia en el camino y da un resultado más
   * limpio, que es lo mismo que hace cualquier redimensionador serio.
   * De paso garantiza que las dos versiones tengan exactamente la misma
   * orientación y el mismo fondo: salen del mismo píxel.
   *
   * ⚠️  SI FALLA, NO SE CAE LA FOTO. Una miniatura que no se pudo
   *     generar significa que la grilla va a mostrar la grande: más
   *     cara, correcta. Perder la foto por no poder achicarla sería
   *     cambiar un problema de plata por uno de datos. */
  let mini = null;
  try {
    const chica = dibujar(grande.lienzo, LADO_MINIATURA, CALIDAD_MINI);
    if (chica.base64) {
      mini = {
        base64: chica.base64,
        contentType: "image/jpeg",
        ancho: chica.ancho,
        alto: chica.alto,
        bytes: chica.bytes,
      };
    }
  } catch {
    mini = null;
  }

  return {
    base64: grande.base64,
    contentType: "image/jpeg",
    ancho: grande.ancho,
    alto: grande.alto,
    bytes: grande.bytes,
    // Sirve como miniatura instantánea: la ficha muestra la foto apenas
    // se elige el archivo, sin esperar a que suba ni a que la IA responda.
    vistaPrevia: grande.dataUrl,
    // La versión de grilla. Puede ser null: ver el comentario de arriba.
    mini,
  };
}

/**
 * Prepara varias, sin que una mala tumbe al resto.
 *
 * Devuelve { listas, fallidas } — `listas` conserva el nombre original
 * del archivo para poder mostrarlo en la ficha.
 */
export async function prepararVarias(archivos, alAvanzar) {
  const listas = [];
  const fallidas = [];

  for (let i = 0; i < archivos.length; i++) {
    const archivo = archivos[i];
    try {
      const foto = await prepararFoto(archivo);
      listas.push({ ...foto, nombreArchivo: archivo.name });
    } catch (e) {
      fallidas.push({
        nombreArchivo: archivo.name,
        /* `ErrorDeImagen` SÍ puede mostrar su `.message`: lo arma
         * este archivo con `t()`, así que ya viene traducido. El
         * respaldo era un literal en español suelto. */
        error:
          e instanceof ErrorDeImagen
            ? e.message
            : t("comun.noSeProceso", "no se pudo procesar"),
      });
    }
    alAvanzar?.(i + 1, archivos.length);
  }

  return { listas, fallidas };
}

export { LADO_MAXIMO, LADO_MINIATURA, CALIDAD_MINI, TIPOS_ACEPTADOS };
